import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog"
import { Plus, Loader2 } from "lucide-react"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { useCreatePlan } from "@/hooks/usePartnerPlans"
import { CreatePlanData } from "@/types/partners"
import { toast } from "sonner"

function CreatePlanDialog({ }: any) {
  // React Query mutation for creating plan
  const createPlanMutation = useCreatePlan();

  const [isCreatePlanOpen, setIsCreatePlanOpen] = useState(false);
  const [planNameError, setPlanNameError] = useState<string>('');
  const [plan, setPlan] = useState({
    planName: '',
    planDescription: '',
    planCredits: '',
    planPrice: '',
  })

  
  const calculatePlanMargin = (credits: number, price: number) => {
    const baseCost = credits * 0.38; // Average $0.38 per call
    return price > 0 ? ((price - baseCost) / price * 100).toFixed(1) : '0';
  };
  const currentPlanMargin = plan.planCredits && plan.planPrice ? 
  calculatePlanMargin(parseInt(plan.planCredits), parseFloat(plan.planPrice)) : '0';
  const currentPlanBaseCost = plan.planCredits ? (parseInt(plan.planCredits) * 0.38).toFixed(2) : '0';
  


  const handleCreatePlan = async () => {
    // Clear previous error
    setPlanNameError('');
    
    if (!plan.planName || !plan.planCredits || !plan.planPrice) return toast.error('Please fill in all required fields');

    // Prepare data for API
    const planData: CreatePlanData = {
      plan_name: plan.planName,
      plan_identifier: plan.planName.toLowerCase().replace(/\s+/g, '-'),
      price_monthly: parseFloat(plan.planPrice),
      monthly_credits: parseInt(plan.planCredits),
      description: plan.planDescription || undefined,
    };

    try {
      await createPlanMutation.mutateAsync(planData);
      setPlan({ planName: '', planDescription: '', planCredits: '',  planPrice: '', });
      setPlanNameError('');
      setIsCreatePlanOpen(false);
    } catch (error) {
      // Handle specific error for plan identifier
      if (error instanceof Error && 
          (error.message.includes('Plan identifier already exists') || 
           error.message.includes('duplicate key') ||
           error.message.includes('already exists'))) {
        setPlanNameError(`Plan with this name is already exists.`);
      } else {
        toast.error('Plan creation failed: ' + error);
      }
    }
  };
  
  return (
    <Dialog open={isCreatePlanOpen} onOpenChange={setIsCreatePlanOpen}>
    <DialogTrigger asChild>
      <Button>
        <Plus className="h-4 w-4 mr-2" />
        Create Plan
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Create Subscription Plan</DialogTitle>
        <DialogDescription>
          Create a new custom subscription plan for your clients
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="planName">Plan Name *</Label>
          <Input
            id="planName"
            value={plan.planName}
            onChange={(e) => {
              setPlan({ ...plan, planName: e.target.value });
              if (planNameError) setPlanNameError('');
            }}
            placeholder="e.g., Starter Pro"
            className={planNameError ? 'border-red-500' : ''}
          />
          {planNameError && (
            <p className="text-sm text-red-500 mt-1">{planNameError}</p>
          )}
        </div>
        <div>
          <Label htmlFor="planDescription">Description</Label>
          <Textarea
            id="planDescription"
            value={plan.planDescription}
            onChange={(e) => setPlan({ ...plan, planDescription: e.target.value })}
            placeholder="Describe the plan features..."
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="planCredits">Monthly Credits *</Label>
            <Input
              id="planCredits"
              type="number"
              value={plan.planCredits}
              onChange={(e) => setPlan({ ...plan, planCredits: e.target.value })}
              placeholder="500"
            />
          </div>
          <div>
            <Label htmlFor="planPrice">Monthly Price ($) *</Label>
            <Input
              id="planPrice"
              type="number"
              step="0.01"
              value={plan.planPrice}
              onChange={(e) => setPlan({ ...plan, planPrice: e.target.value })}
              placeholder="97.00"
            />
          </div>
        </div>
        {plan.planCredits && plan.planPrice && (
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-sm space-y-1">
              <div>Base Cost: ${currentPlanBaseCost}</div>
              <div>Margin: {currentPlanMargin}%</div>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline" 
            onClick={() => setIsCreatePlanOpen(false)}
            disabled={createPlanMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleCreatePlan} disabled={createPlanMutation.isPending}>
            {createPlanMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Plan'
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  )
}

export default CreatePlanDialog